import React, { useState } from 'react'
import { AppDialog, Button, TextInput } from '../../Common'
import { useNls } from '../../../nls/nlsProvider'
import { coSignRecord, proxyCoSignRecord } from '../../../services/opsRegistersService'
import type { OpsRecord } from '../../../types/ops'
import { getApiErrorMessage } from '../../../services/apiConnectivity'

export interface CoSignDialogProps {
  open: boolean
  onDismiss: () => void
  record: OpsRecord
  mode: 'sign' | 'proxy'
  onSuccess?: () => void
}

export const CoSignDialog: React.FC<CoSignDialogProps> = ({
  open,
  onDismiss,
  record,
  mode,
  onSuccess,
}) => {
  const { t } = useNls()
  const [agree, setAgree] = useState<boolean>(true)
  const [reason, setReason] = useState<string>('')
  const [busy, setBusy] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const isProxy = mode === 'proxy'
  const title = isProxy
    ? t('NLSOpsRegisterCoSignProxyDialogTitle')
    : t('NLSOpsRegisterCoSignDialogTitle')

  const isReasonValid = isProxy
    ? reason.trim().length >= 3 && reason.trim().length <= 500
    : agree || (reason.trim().length >= 3 && reason.trim().length <= 500)

  const handleConfirm = async () => {
    if (!isReasonValid) return
    setBusy(true)
    setErrorMsg(null)

    try {
      if (isProxy) {
        await proxyCoSignRecord({
          workspaceId: record.workspaceId,
          recordId: record.id ?? '',
          reason: reason.trim(),
          opId: crypto.randomUUID(),
        })
      } else {
        await coSignRecord({
          workspaceId: record.workspaceId,
          recordId: record.id ?? '',
          agree,
          reason: agree ? undefined : reason.trim(),
          opId: crypto.randomUUID(),
        })
      }
      onSuccess?.()
      onDismiss()
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppDialog
      open={open}
      title={title}
      size="sm"
      onDismiss={onDismiss}
      actions={[
        {
          label: t('NLSOpsRegisterCoSignCancel'),
          variant: 'ghost',
          onClick: onDismiss,
          disabled: busy,
        },
        {
          label: t('NLSOpsRegisterCoSignConfirm'),
          variant: !isProxy && !agree ? 'danger' : 'primary',
          onClick: handleConfirm,
          disabled: busy || !isReasonValid,
          loading: busy,
        },
      ]}
    >
      <div className="opsRegistersDialogBody">
        {errorMsg && (
          <div className="opsRegistersBannerWarn" role="alert">
            {errorMsg}
          </div>
        )}

        {!isProxy && (
          <div className="opsRegistersActionRow">
            <Button
              variant={agree ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setAgree(true)}
            >
              {t('NLSOpsRegisterCoSignActionAgree')}
            </Button>
            <Button
              variant={!agree ? 'danger' : 'secondary'}
              size="sm"
              onClick={() => setAgree(false)}
            >
              {t('NLSOpsRegisterCoSignActionDisagree')}
            </Button>
          </div>
        )}

        {isProxy && (
          <div className="opsRegistersFieldGroup">
            <label htmlFor="ops-proxy-cosign-reason" className="opsRegistersFieldLabel">
              {t('NLSOpsRegisterCoSignProxyReasonLabel')}
            </label>
            <TextInput
              id="ops-proxy-cosign-reason"
              aria-label={t('NLSOpsRegisterCoSignProxyReasonLabel')}
              placeholder={t('NLSOpsRegisterCoSignProxyReasonPlaceholder')}
              value={reason}
              onChange={setReason}
            />
          </div>
        )}

        {!isProxy && !agree && (
          <div className="opsRegistersFieldGroup">
            <label htmlFor="ops-cosign-dispute-reason" className="opsRegistersFieldLabel">
              {t('NLSOpsRegisterCoSignDisputeReasonLabel')}
            </label>
            <TextInput
              id="ops-cosign-dispute-reason"
              aria-label={t('NLSOpsRegisterCoSignDisputeReasonLabel')}
              placeholder={t('NLSOpsRegisterCoSignDisputeReasonPlaceholder')}
              value={reason}
              onChange={setReason}
            />
          </div>
        )}
      </div>
    </AppDialog>
  )
}
